import { useNode } from "@craftjs/core";
import React, { useRef, useState } from "react";
import { Box, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import { useResponsive } from "../../hooks/useResponsive";
import { faqStyles } from "../../config/styleConfig";
import { selectedOutlineSx } from "../../config/settingsStyles";
import { CheckboxField, ColorField, SliderField, SettingsSection } from "../ui/form/SettingsField";
import { labelStyle, inputStyle, itemCardStyle, addButtonStyle, removeButtonStyle } from "../../config/settingsStyles";

export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQProps {
  items: FAQItem[];
  backgroundColor?: string;
  textColor?: string;
  accentColor?: string;
  allowMultiple?: boolean;
  borderRadius?: number;
  padding?: number;
  questionFontSize?: number;
  answerFontSize?: number;
  textAlign?: "left" | "center" | "right";
}

export const FAQ = ({
  items,
  backgroundColor = "#ffffff",
  textColor = "#1f2937",
  accentColor = "#3b82f6",
  allowMultiple = false,
  borderRadius = 8,
  padding = 16,
  questionFontSize = 16,
  answerFontSize = 14,
  textAlign = "left",
}: FAQProps) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useResponsive(containerRef as React.RefObject<HTMLElement>);

  // Use custom font sizes or fallback to responsive defaults
  const defaultQuestionStyle = isMobile ? faqStyles.question.mobile : faqStyles.question.desktop;
  const defaultAnswerStyle = isMobile ? faqStyles.answer.mobile : faqStyles.answer.desktop;

  const actualQuestionFontSize = questionFontSize || defaultQuestionStyle.fontSize;
  const actualAnswerFontSize = answerFontSize || defaultAnswerStyle.fontSize;
  const actualPadding = padding || defaultQuestionStyle.padding;

  const [openIndexes, setOpenIndexes] = useState<number[]>([0]);

  const toggleItem = (index: number) => {
    if (allowMultiple) {
      setOpenIndexes((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
    } else {
      setOpenIndexes((prev) => (prev.includes(index) ? [] : [index]));
    }
  };

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) {
          connect(drag(ref));
          (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = ref;
        }
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        ...(selected && selectedOutlineSx),
      }}
    >
      {items.map((item, index) => (
        <Box
          key={index}
          sx={{
            overflow: "hidden",
            bgcolor: backgroundColor,
            borderRadius: `${borderRadius}px`,
          }}
        >
          <Box
            component="button"
            onClick={() => toggleItem(index)}
            sx={{
              width: "100%",
              p: `${actualPadding}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              textAlign: textAlign,
              color: textColor,
              bgcolor: "transparent",
              border: "none",
              cursor: "pointer",
              transition: "background-color 0.2s",
              "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
            }}
          >
            <Typography
              component="span"
              sx={{
                fontWeight: 500,
                pr: 2,
                fontSize: `${actualQuestionFontSize}px`,
                overflow: "hidden",
                textOverflow: "ellipsis",
                textAlign: textAlign,
                flex: 1,
              }}
            >
              {item.question}
            </Typography>
            <Box
              component="svg"
              sx={{
                width: 20,
                height: 20,
                flexShrink: 0,
                transition: "transform 0.2s",
                transform: openIndexes.includes(index) ? "rotate(180deg)" : "none",
                color: accentColor,
              }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </Box>
          </Box>
          <Box
            sx={{
              overflow: "hidden",
              transition: "max-height 0.3s ease",
              maxHeight: openIndexes.includes(index) ? 384 : 0,
            }}
          >
            <Typography
              sx={{
                px: `${actualPadding}px`,
                pb: `${actualPadding}px`,
                fontSize: `${actualAnswerFontSize}px`,
                lineHeight: 1.7,
                color: textColor,
                opacity: 0.8,
                overflow: "hidden",
                textOverflow: "ellipsis",
                textAlign: textAlign,
              }}
            >
              {item.answer}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export const FAQSettings = () => {
  const {
    actions: { setProp },
    items,
    backgroundColor,
    textColor,
    accentColor,
    allowMultiple,
    borderRadius,
    padding,
    questionFontSize,
    answerFontSize,
    textAlign,
  } = useNode((node) => ({
    items: node.data.props.items,
    backgroundColor: node.data.props.backgroundColor,
    textColor: node.data.props.textColor,
    accentColor: node.data.props.accentColor,
    allowMultiple: node.data.props.allowMultiple,
    borderRadius: node.data.props.borderRadius,
    padding: node.data.props.padding,
    questionFontSize: node.data.props.questionFontSize,
    answerFontSize: node.data.props.answerFontSize,
    textAlign: node.data.props.textAlign,
  }));

  const updateItem = (index: number, field: keyof FAQItem, value: string) => {
    setProp((props: FAQProps) => {
      const newItems = [...props.items];
      newItems[index] = { ...newItems[index], [field]: value };
      props.items = newItems;
    });
  };

  const addItem = () => {
    setProp((props: FAQProps) => {
      props.items = [...props.items, { question: "New Question?", answer: "Answer here..." }];
    });
  };

  const removeItem = (index: number) => {
    setProp((props: FAQProps) => {
      props.items = props.items.filter((_, i) => i !== index);
    });
  };

  return (
    <SettingsSection>
      <div>
        <label style={labelStyle}>FAQ Items</label>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {items.map((item: FAQItem, index: number) => (
            <div key={index} style={itemCardStyle}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "8px",
                }}
              >
                <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>Item {index + 1}</span>
                <button onClick={() => removeItem(index)} style={removeButtonStyle}>
                  Remove
                </button>
              </div>
              <input
                type="text"
                value={item.question}
                onChange={(e) => updateItem(index, "question", e.target.value)}
                style={{ ...inputStyle, marginBottom: "8px" }}
                placeholder="Question"
              />
              <textarea
                value={item.answer}
                onChange={(e) => updateItem(index, "answer", e.target.value)}
                style={{ ...inputStyle, minHeight: "80px", resize: "vertical" }}
                rows={3}
                placeholder="Answer"
              />
            </div>
          ))}
        </div>
        <button onClick={addItem} style={addButtonStyle}>
          + Add FAQ Item
        </button>
      </div>

      <CheckboxField
        label="Allow Multiple Open"
        checked={allowMultiple}
        onChange={(v) => setProp((props: FAQProps) => (props.allowMultiple = v))}
      />

      <ColorField
        label="Background Color"
        value={backgroundColor}
        onChange={(v) => setProp((props: FAQProps) => (props.backgroundColor = v))}
      />

      <ColorField
        label="Text Color"
        value={textColor}
        onChange={(v) => setProp((props: FAQProps) => (props.textColor = v))}
      />

      <ColorField
        label="Accent Color"
        value={accentColor}
        onChange={(v) => setProp((props: FAQProps) => (props.accentColor = v))}
      />

      <SliderField
        label="Border Radius"
        value={borderRadius}
        onChange={(v) => setProp((props: FAQProps) => (props.borderRadius = v))}
        min={0}
        max={24}
        unit="px"
      />

      <SliderField
        label="Padding"
        value={padding}
        onChange={(v) => setProp((props: FAQProps) => (props.padding = v))}
        min={8}
        max={48}
        unit="px"
      />

      <SliderField
        label="Question Font Size"
        value={questionFontSize}
        onChange={(v) => setProp((props: FAQProps) => (props.questionFontSize = v))}
        min={12}
        max={28}
        unit="px"
      />

      <SliderField
        label="Answer Font Size"
        value={answerFontSize}
        onChange={(v) => setProp((props: FAQProps) => (props.answerFontSize = v))}
        min={12}
        max={24}
        unit="px"
      />

      <Box>
        <Typography variant="body2" gutterBottom>
          Text Align
        </Typography>
        <ToggleButtonGroup
          value={textAlign || "left"}
          exclusive
          onChange={(_, value) => {
            if (value !== null) {
              setProp((props: FAQProps) => (props.textAlign = value as FAQProps["textAlign"]));
            }
          }}
          size="small"
          fullWidth
        >
          <ToggleButton value="left" sx={{ flex: 1 }}>
            <FormatAlignLeftIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="center" sx={{ flex: 1 }}>
            <FormatAlignCenterIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="right" sx={{ flex: 1 }}>
            <FormatAlignRightIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </SettingsSection>
  );
};

FAQ.craft = {
  displayName: "FAQ",
  props: {
    items: [
      {
        question: "What services do you offer?",
        answer:
          "We offer a comprehensive range of dermatology, aesthetic medicine, and wellness services including skin treatments, anti-aging procedures, and preventive care programs.",
      },
      {
        question: "How do I book an appointment?",
        answer:
          "You can book an appointment by calling our clinic, using our online booking system, or sending us a message through the contact form on this website.",
      },
      {
        question: "What are your operating hours?",
        answer:
          "We are open Monday to Friday from 9:00 AM to 7:00 PM, Saturday from 9:00 AM to 5:00 PM, and Sunday from 10:00 AM to 2:00 PM.",
      },
    ],
    backgroundColor: "#ffffff",
    textColor: "#1f2937",
    accentColor: "#3b82f6",
    allowMultiple: false,
    borderRadius: 8,
    padding: 16,
    questionFontSize: 16,
    answerFontSize: 14,
    textAlign: "left",
  },
  related: {
    settings: FAQSettings,
  },
};
